import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeviceChartDataResponse } from "types/deviceHooks.types";
import { TimeRange } from "components/charts/TimeRangeNavigator";
import {
  ALL_TIME_LOOKBACK_MS,
  useChartChunkQueries,
} from "./useChartChunkQueries";

const INITIAL_LOAD_MS = 91 * 24 * 60 * 60 * 1000;
const CACHE_EXTEND_DEBOUNCE_MS = 450;

interface UseDeviceChartCacheArgs {
  serialNumber: string;
  timeRangeValue: number | string;
  fromTimestamp: number;
  toTimestamp: number;
  handleTimeRangeChange: (range: TimeRange) => void;
  returnToNow: () => void;
  isAtCurrentTime: () => boolean;
  handleResetZoom: () => void;
}

export function useDeviceChartCache({
  serialNumber,
  timeRangeValue,
  fromTimestamp,
  toTimestamp,
  handleTimeRangeChange,
  returnToNow,
  isAtCurrentTime,
  handleResetZoom,
}: UseDeviceChartCacheArgs) {
  const initialWindowRef = useRef({
    from: Date.now() - INITIAL_LOAD_MS,
    to: Date.now(),
  });
  const [cache, setCache] = useState<{ dataPoints: number[][]; manifest: any }>({
    dataPoints: [],
    manifest: null,
  });
  const [cacheFrom, setCacheFrom] = useState(initialWindowRef.current.from);
  const cacheFromRef = useRef(cacheFrom);
  cacheFromRef.current = cacheFrom;

  const lastProcessedChunkKeyRef = useRef<string | null>(null);
  const [chunkFetch, setChunkFetch] = useState({
    from: initialWindowRef.current.from,
    to: initialWindowRef.current.to,
  });
  const chunkFetchKey = `${chunkFetch.from}:${chunkFetch.to}`;
  const isAllTimeView = timeRangeValue === "all";

  const { chunkQuery, allTimeQuery } = useChartChunkQueries(
    serialNumber,
    isAllTimeView,
    chunkFetch,
  );

  useEffect(() => {
    if (!chunkQuery.data?.dataPointHistory?.dataPoints) return;
    if (lastProcessedChunkKeyRef.current === chunkFetchKey) return;
    lastProcessedChunkKeyRef.current = chunkFetchKey;

    const newPoints = chunkQuery.data.dataPointHistory.dataPoints;
    const newManifest = chunkQuery.data.dataPointHistory.manifest;
    const fetchedFrom = chunkFetch.from;

    setCache((prev) => {
      if (prev.dataPoints.length === 0) {
        return {
          dataPoints: [...newPoints].sort((a, b) => a[0] - b[0]),
          manifest: newManifest,
        };
      }
      const pointsToAdd = newPoints.filter((p) => p[0] < cacheFromRef.current);
      if (pointsToAdd.length === 0) return { ...prev, manifest: newManifest };
      return {
        dataPoints: [
          ...pointsToAdd.sort((a, b) => a[0] - b[0]),
          ...prev.dataPoints,
        ],
        manifest: newManifest,
      };
    });
    setCacheFrom((prev) => Math.min(prev, fetchedFrom));
  }, [chunkQuery.data, chunkFetchKey, chunkFetch.from]);

  useEffect(() => {
    if (chunkQuery.isError) lastProcessedChunkKeyRef.current = null;
  }, [chunkQuery.isError, chunkFetchKey]);

  const [settledFromTimestamp, setSettledFromTimestamp] =
    useState(fromTimestamp);
  useEffect(() => {
    const timer = setTimeout(
      () => setSettledFromTimestamp(fromTimestamp),
      CACHE_EXTEND_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [fromTimestamp]);

  const requestCacheExtend = useCallback(
    (neededFrom: number) => {
      if (
        isAllTimeView ||
        chunkQuery.isFetching ||
        cache.dataPoints.length === 0
      ) {
        return;
      }
      if (neededFrom >= cacheFromRef.current) return;

      const missingTo = cacheFromRef.current;
      const missingFrom = Math.max(
        neededFrom,
        missingTo - ALL_TIME_LOOKBACK_MS,
      );
      if (missingFrom >= missingTo) return;
      if (
        chunkFetch.from === missingFrom &&
        chunkFetch.to === missingTo &&
        !chunkQuery.isError
      ) {
        return;
      }
      setChunkFetch({ from: missingFrom, to: missingTo });
    },
    [
      isAllTimeView,
      chunkQuery.isFetching,
      chunkQuery.isError,
      cache.dataPoints.length,
      chunkFetch.from,
      chunkFetch.to,
    ],
  );

  useEffect(() => {
    if (!settledFromTimestamp || isAllTimeView) return;
    requestCacheExtend(settledFromTimestamp);
  }, [settledFromTimestamp, cacheFrom, isAllTimeView, requestCacheExtend]);

  const [isChartPanned, setIsChartPanned] = useState(false);
  const isChartPannedRef = useRef(false);
  const viewportFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (viewportFetchTimerRef.current) {
        clearTimeout(viewportFetchTimerRef.current);
      }
    },
    [],
  );

  const handleViewportChange = useCallback(
    (minTimestamp: number) => {
      if (!isChartPannedRef.current) {
        isChartPannedRef.current = true;
        setIsChartPanned(true);
      }
      if (viewportFetchTimerRef.current) {
        clearTimeout(viewportFetchTimerRef.current);
      }
      viewportFetchTimerRef.current = setTimeout(() => {
        requestCacheExtend(minTimestamp);
      }, CACHE_EXTEND_DEBOUNCE_MS);
    },
    [requestCacheExtend],
  );

  const handleReturnToNow = useCallback(() => {
    // Clear pan flag before resetZoom so a late zoom callback cannot re-enable NOW
    isChartPannedRef.current = false;
    setIsChartPanned(false);
    returnToNow();
    handleResetZoom();
  }, [returnToNow, handleResetZoom]);

  const isAtCurrentTimeWithPan = useCallback(() => {
    if (isChartPanned) return false;
    return isAtCurrentTime();
  }, [isChartPanned, isAtCurrentTime]);

  const handleTimeRangeChangeWithReset = useCallback(
    (range: TimeRange) => {
      isChartPannedRef.current = false;
      setIsChartPanned(false);
      handleResetZoom();
      handleTimeRangeChange(range);
    },
    [handleTimeRangeChange, handleResetZoom],
  );

  const isExtendingCache =
    !isAllTimeView && chunkQuery.isFetching && cache.dataPoints.length > 0;
  const isLoading = isAllTimeView
    ? allTimeQuery.isLoading && !allTimeQuery.data
    : chunkQuery.isLoading && cache.dataPoints.length === 0;
  const isFetching = isAllTimeView
    ? allTimeQuery.isFetching
    : (chunkQuery.isFetching && cache.dataPoints.length === 0) ||
      isExtendingCache;
  const error = isAllTimeView
    ? allTimeQuery.error
    : cache.dataPoints.length === 0
      ? chunkQuery.error
      : chunkQuery.isError
        ? chunkQuery.error
        : null;

  const allTimePoints = allTimeQuery.data?.dataPointHistory?.dataPoints ?? [];
  const data = useMemo(() => {
    if (isAllTimeView) return allTimeQuery.data ?? null;
    if (!cache.manifest) return null;
    return {
      dataPointHistory: {
        manifest: cache.manifest,
        dataPoints: cache.dataPoints,
      },
    } as DeviceChartDataResponse;
  }, [isAllTimeView, allTimeQuery.data, cache]);

  return {
    data,
    error,
    isLoading,
    isFetching,
    isExtendingCache,
    effectiveFromTimestamp: isAllTimeView
      ? (allTimePoints[0]?.[0] ?? fromTimestamp)
      : fromTimestamp,
    effectiveToTimestamp: isAllTimeView
      ? (allTimePoints[allTimePoints.length - 1]?.[0] ?? toTimestamp)
      : toTimestamp,
    handleViewportChange,
    handleReturnToNow,
    isAtCurrentTimeWithPan,
    isChartPanned,
    handleTimeRangeChangeWithReset,
  };
}
